import { has } from "lodash";
import { Country } from "@linkfluence/models/country";
import { AbstractDate } from "@linkfluence/models/date";
import { Metric } from "@linkfluence/models/metric";
import { Clause } from "@radarly/models/clause";
import {
  Dataviz,
  DatavizName,
  getDefaultDataviz,
} from "@radarly/models/dataviz";
import { SearchFragmentEmv } from "@radarly/models/search";
import { isEmvCardDataviz } from "../dataviz";
import { Card, EmvCard } from "../model";
import { ComparisonPeriod } from "../period";
import { CardFactory, CardFragmentsFactory } from "./base";
import { ICardFactory } from "./model";

interface EmvCardFactoryOptions {
  from?: AbstractDate;
  to?: AbstractDate;
  clause?: Clause;
  comparisonPeriod?: ComparisonPeriod;
}

export class EmvCardFragmentsFactory extends CardFragmentsFactory {
  static create(input?: EmvCardFactoryOptions) {
    const factory = new EmvCardFragmentsFactory();
    return factory.getFragments(input);
  }
  static createFromCard(card: Card, input?: EmvCardFactoryOptions) {
    const factory = new EmvCardFragmentsFactory();
    const { from, to } = factory.getPeriod(card);
    return factory.getFragments({
      clause: factory.getClauseFromCard(card),
      from,
      to,
      comparisonPeriod: factory.getComparaisonPeriod(
        card,
        input && has(input, "comparisonPeriod")
          ? input?.comparisonPeriod
          : "previous_period",
        input
      ),
    });
  }
  private getFragments(input?: EmvCardFactoryOptions) {
    return this.getComparaisonFragments(
      this.getFragment(input),
      input && has(input, "comparisonPeriod")
        ? input?.comparisonPeriod
        : "previous_period"
    );
  }
  private getFragment(
    input?: EmvCardFactoryOptions
  ): SearchFragmentEmv<Metric[]> {
    return {
      request: {
        query: this.getQuery(input),
        aggs: {
          date: {
            metrics: [Metric.Impression, Metric.Reach],
            interval: this.getInterval({ from: input?.from, to: input?.to }),
            aggs: {
              platforms: {
                metrics: [Metric.Impression, Metric.Reach],
                aggs: {
                  countries: {
                    metrics: [Metric.Impression, Metric.Reach],
                  },
                },
              },
            },
          },
        },
      },
      type: "emv",
    };
  }
}

export class EmvCardFactory
  implements ICardFactory<EmvCard, EmvCardFactoryOptions>
{
  public static getInstance() {
    return new EmvCardFactory();
  }
  public fromCard(
    workspaceId: number,
    projectFocuses: number[],
    card: Card,
    input?: EmvCardFactoryOptions
  ) {
    return CardFactory.getInstance<EmvCard>().createCard(
      workspaceId,
      projectFocuses,
      "emv",
      card.title,
      EmvCardFragmentsFactory.createFromCard(card, input),
      this.createDataviz(card.dataviz)
    );
  }
  public fromInput(
    workspaceId: number,
    projectFocuses: number[],
    title: string,
    input?: EmvCardFactoryOptions & {
      fullDataviz?: Dataviz;
    }
  ) {
    return CardFactory.getInstance<EmvCard>().createCard(
      workspaceId,
      projectFocuses,
      "emv",
      title,
      EmvCardFragmentsFactory.create(input),
      this.createDataviz(
        input?.fullDataviz ?? getDefaultDataviz(DatavizName.Emv)
      )
    );
  }
  public createDataviz(dataviz: Dataviz) {
    if (isEmvCardDataviz(dataviz)) {
      return dataviz;
    } else {
      return getDefaultDataviz(DatavizName.Emv) as EmvCard["dataviz"];
    }
  }
}

export enum CashType {
  Print = "print",
  Social = "social",
  Web = "web",
}

export const countriesEMV = {
  [Country.AU]: {
    // australia
    [CashType.Print]: 44.79,
    [CashType.Social]: 40.2,
    [CashType.Web]: 37.9,
  },
  [Country.BH]: {
    // bahrain
    [CashType.Print]: 58.44,
    [CashType.Social]: 15.16,
    [CashType.Web]: 14.33,
  },
  [Country.BY]: {
    // belarus
    [CashType.Print]: 56.32,
    [CashType.Social]: 8.08,
    [CashType.Web]: 4.83,
  },
  [Country.BE]: {
    // belgium
    [CashType.Print]: 44.39,
    [CashType.Social]: 24.64,
    [CashType.Web]: 18.27,
  },
  [Country.BR]: {
    // brazil
    [CashType.Print]: 67.16,
    [CashType.Social]: 6.1,
    [CashType.Web]: 5.23,
  },
  [Country.CA]: {
    // canada
    [CashType.Print]: 57.66,
    [CashType.Social]: 18.74,
    [CashType.Web]: 24.49,
  },
  [Country.CL]: {
    // chile
    [CashType.Print]: 57.66,
    [CashType.Social]: 5.64,
    [CashType.Web]: 7.83,
  },
  [Country.CN]: {
    // china
    [CashType.Print]: 23.75,
    [CashType.Social]: 9.86,
    [CashType.Web]: 14.33,
  },
  [Country.CO]: {
    // colombia
    [CashType.Print]: 57.66,
    [CashType.Social]: 4.42,
    [CashType.Web]: 3.35,
  },
  [Country.CZ]: {
    // czech republic
    [CashType.Print]: 56.32,
    [CashType.Social]: 15.16,
    [CashType.Web]: 14.33,
  },
  [Country.DK]: {
    // denmark
    [CashType.Print]: 57.66,
    [CashType.Social]: 42.95,
    [CashType.Web]: 28.94,
  },
  [Country.IE]: {
    // ireland
    [CashType.Print]: 57.66,
    [CashType.Social]: 42.95,
    [CashType.Web]: 28.94,
  },
  [Country.EE]: {
    // estonia
    [CashType.Print]: 56.32,
    [CashType.Social]: 15.16,
    [CashType.Web]: 11.77,
  },
  [Country.FR]: {
    // france
    [CashType.Print]: 33.21,
    [CashType.Social]: 34.11,
    [CashType.Web]: 21.61,
  },
  [Country.DE]: {
    // germany
    [CashType.Print]: 44.39,
    [CashType.Social]: 34.94,
    [CashType.Web]: 25.83,
  },
  [Country.GR]: {
    // greece
    [CashType.Print]: 58.44,
    [CashType.Social]: 8.93,
    [CashType.Web]: 10.27,
  },
  [Country.HK]: {
    // hong-kong
    [CashType.Print]: 57.66,
    [CashType.Social]: 34.94,
    [CashType.Web]: 25.83,
  },
  [Country.HU]: {
    // hungary
    [CashType.Print]: 56.32,
    [CashType.Social]: 8.29,
    [CashType.Web]: 8.09,
  },
  [Country.IS]: {
    // iceland
    [CashType.Print]: 56.32,
    [CashType.Social]: 30.66,
    [CashType.Web]: 28.94,
  },
  [Country.IN]: {
    // india
    [CashType.Print]: 57.66,
    [CashType.Social]: 4.88,
    [CashType.Web]: 4.93,
  },
  [Country.IL]: {
    // israel
    [CashType.Print]: 57.66,
    [CashType.Social]: 18.74,
    [CashType.Web]: 24.49,
  },
  [Country.IT]: {
    // italy
    [CashType.Print]: 62.45,
    [CashType.Social]: 11.7,
    [CashType.Web]: 17.99,
  },
  [Country.JP]: {
    // japan
    [CashType.Print]: 33.21,
    [CashType.Social]: 34.11,
    [CashType.Web]: 21.61,
  },
  [Country.JO]: {
    // jordan
    [CashType.Print]: 57.66,
    [CashType.Social]: 5.36,
    [CashType.Web]: 5.54,
  },
  [Country.KZ]: {
    // kazakhstan
    [CashType.Print]: 56.32,
    [CashType.Social]: 4.45,
    [CashType.Web]: 6.13,
  },
  [Country.KW]: {
    // kuwait
    [CashType.Print]: 57.66,
    [CashType.Social]: 15.16,
    [CashType.Web]: 14.33,
  },
  [Country.LB]: {
    // lebanon
    [CashType.Print]: 57.66,
    [CashType.Social]: 4.88,
    [CashType.Web]: 4.93,
  },
  [Country.LT]: {
    // lithuania
    [CashType.Print]: 56.32,
    [CashType.Social]: 13.61,
    [CashType.Web]: 8.79,
  },
  [Country.LU]: {
    // luxemburg
    [CashType.Print]: 30.3,
    [CashType.Social]: 44.22,
    [CashType.Web]: 27.09,
  },
  [Country.MY]: {
    // malaysia
    [CashType.Print]: 23.75,
    [CashType.Social]: 9.86,
    [CashType.Web]: 14.33,
  },
  [Country.MX]: {
    // mexico
    [CashType.Print]: 54.76,
    [CashType.Social]: 5.39,
    [CashType.Web]: 5.23,
  },
  [Country.NL]: {
    // netherlands
    [CashType.Print]: 79.02,
    [CashType.Social]: 32.88,
    [CashType.Web]: 24.52,
  },
  [Country.NG]: {
    // nigeria
    [CashType.Print]: 56.32,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.NO]: {
    // norway
    [CashType.Print]: 56.32,
    [CashType.Social]: 44.22,
    [CashType.Web]: 29.11,
  },
  [Country.PA]: {
    // panama
    [CashType.Print]: 57.66,
    [CashType.Social]: 5.64,
    [CashType.Web]: 7.83,
  },
  [Country.PE]: {
    // peru
    [CashType.Print]: 48.9,
    [CashType.Social]: 4.54,
    [CashType.Web]: 4.45,
  },
  [Country.PL]: {
    // poland
    [CashType.Print]: 58.44,
    [CashType.Social]: 8.29,
    [CashType.Web]: 8.09,
  },
  [Country.PT]: {
    // portugal
    [CashType.Print]: 58.44,
    [CashType.Social]: 8.82,
    [CashType.Web]: 15.49,
  },
  [Country.QA]: {
    // qatar
    [CashType.Print]: 75.18,
    [CashType.Social]: 32.88,
    [CashType.Web]: 24.52,
  },
  [Country.RO]: {
    // romania
    [CashType.Print]: 57.66,
    [CashType.Social]: 9.26,
    [CashType.Web]: 7.3,
  },
  [Country.RU]: {
    // russia
    [CashType.Print]: 64,
    [CashType.Social]: 9.86,
    [CashType.Web]: 14.33,
  },
  [Country.SA]: {
    // saudi arabia
    [CashType.Print]: 57.66,
    [CashType.Social]: 15.16,
    [CashType.Web]: 14.33,
  },
  [Country.RS]: {
    // serbia
    [CashType.Print]: 56.32,
    [CashType.Social]: 8.08,
    [CashType.Web]: 4.83,
  },
  [Country.SG]: {
    // singapore
    [CashType.Print]: 57.66,
    [CashType.Social]: 30.66,
    [CashType.Web]: 28.94,
  },
  [Country.ZA]: {
    // south africa
    [CashType.Print]: 11.59,
    [CashType.Social]: 6.1,
    [CashType.Web]: 5.23,
  },
  [Country.KR]: {
    // south korea
    [CashType.Print]: 57.66,
    [CashType.Social]: 11.7,
    [CashType.Web]: 17.99,
  },
  [Country.ES]: {
    // spain
    [CashType.Print]: 85.81,
    [CashType.Social]: 12.12,
    [CashType.Web]: 20.77,
  },
  [Country.SE]: {
    // sweden
    [CashType.Print]: 56.32,
    [CashType.Social]: 44.22,
    [CashType.Web]: 26.11,
  },
  [Country.CH]: {
    // switzerland
    [CashType.Print]: 30.3,
    [CashType.Social]: 44.22,
    [CashType.Web]: 27.09,
  },
  [Country.TR]: {
    // turkey
    [CashType.Print]: 54.76,
    [CashType.Social]: 4.45,
    [CashType.Web]: 4.28,
  },
  [Country.UA]: {
    // ukraine
    [CashType.Print]: 57.66,
    [CashType.Social]: 5.36,
    [CashType.Web]: 5.54,
  },
  [Country.AE]: {
    // united arab emirates
    [CashType.Print]: 98.58,
    [CashType.Social]: 17.23,
    [CashType.Web]: 14.33,
  },
  [Country.GB]: {
    // united kingdom
    [CashType.Print]: 51.91,
    [CashType.Social]: 36.24,
    [CashType.Web]: 25.83,
  },
  [Country.US]: {
    // usa
    [CashType.Print]: 123.65,
    [CashType.Social]: 44.22,
    [CashType.Web]: 37.99,
  },
  [Country.AI]: {
    // anguilla
    [CashType.Print]: 0,
    [CashType.Social]: 36.24,
    [CashType.Web]: 25.83,
  },
  [Country.AF]: {
    // afghanistan
    [CashType.Print]: 0,
    [CashType.Social]: 4.45,
    [CashType.Web]: 6.13,
  },
  [Country.AM]: {
    // armenia
    [CashType.Print]: 0,
    [CashType.Social]: 5.39,
    [CashType.Web]: 5.23,
  },
  [Country.AO]: {
    // angola
    [CashType.Print]: 0,
    [CashType.Social]: 5.36,
    [CashType.Web]: 5.54,
  },
  [Country.AZ]: {
    // azerbaijan
    [CashType.Print]: 0,
    [CashType.Social]: 4.45,
    [CashType.Web]: 6.13,
  },
  [Country.BB]: {
    // barbados
    [CashType.Print]: 0,
    [CashType.Social]: 15.16,
    [CashType.Web]: 11.77,
  },
  [Country.BD]: {
    // bangladesh
    [CashType.Print]: 0,
    [CashType.Social]: 5.36,
    [CashType.Web]: 5.54,
  },
  [Country.BF]: {
    // burkina faso
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.BG]: {
    // bulgaria
    [CashType.Print]: 0,
    [CashType.Social]: 13.61,
    [CashType.Web]: 8.79,
  },
  [Country.BO]: {
    // bolivia
    [CashType.Print]: 0,
    [CashType.Social]: 6.1,
    [CashType.Web]: 5.23,
  },
  [Country.BS]: {
    // bahamas
    [CashType.Print]: 0,
    [CashType.Social]: 42.95,
    [CashType.Web]: 28.94,
  },
  [Country.BT]: {
    // buthan
    [CashType.Print]: 0,
    [CashType.Social]: 6.1,
    [CashType.Web]: 5.23,
  },
  [Country.CC]: {
    // cocos
    [CashType.Print]: 0,
    [CashType.Social]: 40.2,
    [CashType.Web]: 37.9,
  },
  [Country.CD]: {
    // democratic republic of the congo
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.DZ]: {
    // algeria
    [CashType.Print]: 0,
    [CashType.Social]: 6.1,
    [CashType.Web]: 5.23,
  },
  [Country.LK]: {
    // sri lanka
    [CashType.Print]: 0,
    [CashType.Social]: 5.39,
    [CashType.Web]: 5.23,
  },
  [Country.VE]: {
    // venezuela
    [CashType.Print]: 0,
    [CashType.Social]: 5.36,
    [CashType.Web]: 5.54,
  },
  [Country.ID]: {
    // indonesia
    [CashType.Print]: 0,
    [CashType.Social]: 5.39,
    [CashType.Web]: 5.23,
  },
  [Country.MA]: {
    // morocco
    [CashType.Print]: 0,
    [CashType.Social]: 6.1,
    [CashType.Web]: 5.23,
  },
  [Country.IR]: {
    // iran
    [CashType.Print]: 0,
    [CashType.Social]: 8.29,
    [CashType.Web]: 8.09,
  },
  [Country.TH]: {
    // thailand
    [CashType.Print]: 0,
    [CashType.Social]: 8.29,
    [CashType.Web]: 8.09,
  },
  [Country.VN]: {
    //vietnam
    [CashType.Print]: 0,
    [CashType.Social]: 6.1,
    [CashType.Web]: 5.23,
  },
  [Country.PH]: {
    //philippines
    [CashType.Print]: 0,
    [CashType.Social]: 6.1,
    [CashType.Web]: 5.23,
  },
  [Country.DO]: {
    //dominican republic
    [CashType.Print]: 0,
    [CashType.Social]: 8.29,
    [CashType.Web]: 8.09,
  },
  [Country.EC]: {
    //ecuador
    [CashType.Print]: 0,
    [CashType.Social]: 5.64,
    [CashType.Web]: 7.83,
  },
  [Country.PY]: {
    // paraguay
    [CashType.Print]: 0,
    [CashType.Social]: 9.86,
    [CashType.Web]: 14.33,
  },
  [Country.EG]: {
    // egypt
    [CashType.Print]: 0,
    [CashType.Social]: 6.1,
    [CashType.Web]: 5.23,
  },
  [Country.KE]: {
    // kenya
    [CashType.Print]: 0,
    [CashType.Social]: 5.36,
    [CashType.Web]: 5.54,
  },
  [Country.TN]: {
    // tunisia
    [CashType.Print]: 0,
    [CashType.Social]: 6.1,
    [CashType.Web]: 5.23,
  },
  [Country.IQ]: {
    // iraq
    [CashType.Print]: 0,
    [CashType.Social]: 4.45,
    [CashType.Web]: 6.13,
  },
  [Country.TW]: {
    // taiwan
    [CashType.Print]: 0,
    [CashType.Social]: 30.66,
    [CashType.Web]: 28.94,
  },
  [Country.UY]: {
    // uruguay
    [CashType.Print]: 0,
    [CashType.Social]: 17.23,
    [CashType.Web]: 14.33,
  },
  [Country.NZ]: {
    // new zeland
    [CashType.Print]: 0,
    [CashType.Social]: 44.22,
    [CashType.Web]: 27.09,
  },
  [Country.GH]: {
    // ghana
    [CashType.Print]: 0,
    [CashType.Social]: 5.36,
    [CashType.Web]: 5.54,
  },
  [Country.CI]: {
    // cote d'ivoire
    [CashType.Print]: 0,
    [CashType.Social]: 6.1,
    [CashType.Web]: 5.23,
  },
  [Country.FI]: {
    // finland
    [CashType.Print]: 0,
    [CashType.Social]: 44.22,
    [CashType.Web]: 27.09,
  },
  [Country.KM]: {
    // comoros
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.GT]: {
    // guatemala
    [CashType.Print]: 0,
    [CashType.Social]: 4.45,
    [CashType.Web]: 6.13,
  },
  [Country.CR]: {
    // costa rica
    [CashType.Print]: 0,
    [CashType.Social]: 15.16,
    [CashType.Web]: 11.77,
  },
  [Country.TT]: {
    // trinidad and tobago
    [CashType.Print]: 0,
    [CashType.Social]: 17.23,
    [CashType.Web]: 14.33,
  },
  [Country.BA]: {
    // bosnia and herzevogovina
    [CashType.Print]: 0,
    [CashType.Social]: 5.64,
    [CashType.Web]: 7.83,
  },
  [Country.CY]: {
    // cyprus
    [CashType.Print]: 0,
    [CashType.Social]: 32.88,
    [CashType.Web]: 24.52,
  },
  [Country.SV]: {
    // el salvador
    [CashType.Print]: 0,
    [CashType.Social]: 5.39,
    [CashType.Web]: 5.23,
  },
  [Country.MU]: {
    // mauritius
    [CashType.Print]: 0,
    [CashType.Social]: 8.93,
    [CashType.Web]: 10.27,
  },
  [Country.JM]: {
    // jamaica
    [CashType.Print]: 0,
    [CashType.Social]: 9.86,
    [CashType.Web]: 14.33,
  },
  [Country.CM]: {
    // cameroon
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 5.23,
  },
  [Country.GE]: {
    // georgia
    [CashType.Print]: 0,
    [CashType.Social]: 4.45,
    [CashType.Web]: 6.13,
  },
  [Country.PK]: {
    // pakistan
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.MT]: {
    // malta
    [CashType.Print]: 0,
    [CashType.Social]: 30.66,
    [CashType.Web]: 28.94,
  },
  [Country.AL]: {
    // albania
    [CashType.Print]: 0,
    [CashType.Social]: 9.86,
    [CashType.Web]: 14.33,
  },
  [Country.GY]: {
    // guyana
    [CashType.Print]: 0,
    [CashType.Social]: 8.29,
    [CashType.Web]: 8.09,
  },
  [Country.MK]: {
    // macedonia
    [CashType.Print]: 0,
    [CashType.Social]: 5.64,
    [CashType.Web]: 7.83,
  },
  [Country.KH]: {
    // cambodia
    [CashType.Print]: 0,
    [CashType.Social]: 4.88,
    [CashType.Web]: 4.93,
  },
  [Country.GP]: {
    // guadeloupe
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.MV]: {
    // maldives
    [CashType.Print]: 0,
    [CashType.Social]: 13.61,
    [CashType.Web]: 8.79,
  },
  [Country.LY]: {
    // libya
    [CashType.Print]: 0,
    [CashType.Social]: 6.1,
    [CashType.Web]: 5.23,
  },
  [Country.PS]: {
    // palestine
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.SN]: {
    // senegal
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.MQ]: {
    // martinique
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.HN]: {
    // honduras
    [CashType.Print]: 0,
    [CashType.Social]: 4.42,
    [CashType.Web]: 3.35,
  },
  [Country.TZ]: {
    // tanzania
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.VC]: {
    // saint vincent and the grenadines
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.NC]: {
    // new caledonia
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.MG]: {
    // madagascar
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.MM]: {
    // myanmar
    [CashType.Print]: 0,
    [CashType.Social]: 4.88,
    [CashType.Web]: 4.93,
  },
  [Country.BJ]: {
    // benin
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.PR]: {
    // puerto rico
    [CashType.Print]: 0,
    [CashType.Social]: 44.22,
    [CashType.Web]: 27.09,
  },
  [Country.SI]: {
    // slovenia
    [CashType.Print]: 0,
    [CashType.Social]: 32.88,
    [CashType.Web]: 24.52,
  },
  [Country.KP]: {
    // north korea
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.SK]: {
    // slovakia
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.SR]: {
    // suriname
    [CashType.Print]: 0,
    [CashType.Social]: 5.39,
    [CashType.Web]: 5.23,
  },
  [Country.CF]: {
    // central african republic
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.CU]: {
    // cuba
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.RE]: {
    // reunion
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.MC]: {
    // monaco
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.UG]: {
    // uganda
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.GN]: {
    // guinea
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.SM]: {
    // san marino
    [CashType.Print]: 0,
    [CashType.Social]: 44.22,
    [CashType.Web]: 27.09,
  },
  [Country.CW]: {
    // curaçao
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.OM]: {
    // oman
    [CashType.Print]: 0,
    [CashType.Social]: 15.16,
    [CashType.Web]: 11.77,
  },
  [Country.ZM]: {
    // zambia
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.AD]: {
    // andorra
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.NA]: {
    // namibia
    [CashType.Print]: 0,
    [CashType.Social]: 5.39,
    [CashType.Web]: 5.23,
  },
  [Country.ZW]: {
    // zimbabwe
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.HT]: {
    // haiti
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.KN]: {
    // saint kitts and nevis
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.SS]: {
    // south sudan
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.AG]: {
    // antigua anbd barbuda
    [CashType.Print]: 0,
    [CashType.Social]: 15.16,
    [CashType.Web]: 11.77,
  },
  [Country.BW]: {
    // bostwana
    [CashType.Print]: 0,
    [CashType.Social]: 5.64,
    [CashType.Web]: 7.83,
  },
  [Country.NI]: {
    // nicaragua
    [CashType.Print]: 0,
    [CashType.Social]: 5.36,
    [CashType.Web]: 5.54,
  },
  [Country.HR]: {
    // croatia
    [CashType.Print]: 0,
    [CashType.Social]: 15.16,
    [CashType.Web]: 11.77,
  },
  [Country.VI]: {
    // virgin islands
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.MD]: {
    // moldova
    [CashType.Print]: 0,
    [CashType.Social]: 4.45,
    [CashType.Web]: 6.13,
  },
  [Country.LV]: {
    // latvia
    [CashType.Print]: 0,
    [CashType.Social]: 17.23,
    [CashType.Web]: 14.33,
  },
  [Country.MZ]: {
    // mozambique
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.GF]: {
    // rench huiana
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.NP]: {
    // nepal
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.UZ]: {
    // uzbekistan
    [CashType.Print]: 0,
    [CashType.Social]: 5.36,
    [CashType.Web]: 5.54,
  },
  [Country.TV]: {
    // tuvalu
    [CashType.Print]: 0,
    [CashType.Social]: 4.45,
    [CashType.Web]: 6.13,
  },
  [Country.SD]: {
    // sudan
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.GA]: {
    // gabon
    [CashType.Print]: 0,
    [CashType.Social]: 8.29,
    [CashType.Web]: 8.09,
  },
  [Country.NE]: {
    // niger
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.LC]: {
    // santa lucia
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.TL]: {
    // timor leste
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.LA]: {
    // laos
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.SY]: {
    // syria
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.ML]: {
    // mali
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.BN]: {
    // brunei
    [CashType.Print]: 0,
    [CashType.Social]: 32.88,
    [CashType.Web]: 24.52,
  },
  [Country.YE]: {
    // yemen
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.FM]: {
    // micronesia
    [CashType.Print]: 0,
    [CashType.Social]: 5.39,
    [CashType.Web]: 5.23,
  },
  [Country.ET]: {
    // ethiopia
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.SL]: {
    // sierra leone
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.PF]: {
    // french polynesia
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.BI]: {
    // burundi
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.CG]: {
    // congo
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.KY]: {
    // caiman islands
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.SB]: {
    // solomon islands
    [CashType.Print]: 0,
    [CashType.Social]: 6.1,
    [CashType.Web]: 5.23,
  },
  [Country.MW]: {
    // malawi
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.DJ]: {
    // djibouti
    [CashType.Print]: 0,
    [CashType.Social]: 8.08,
    [CashType.Web]: 4.83,
  },
  [Country.MR]: {
    // mauritania
    [CashType.Print]: 0,
    [CashType.Social]: 5.36,
    [CashType.Web]: 5.54,
  },
  [Country.BM]: {
    // bermuda
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.PG]: {
    // papua new guinea
    [CashType.Print]: 0,
    [CashType.Social]: 6.1,
    [CashType.Web]: 5.23,
  },
  [Country.RW]: {
    // rwanda
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.TG]: {
    // togo
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.MH]: {
    // marshall islands
    [CashType.Print]: 0,
    [CashType.Social]: 4.45,
    [CashType.Web]: 6.13,
  },
  [Country.PM]: {
    // saint pierre and miquelon
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.WS]: {
    // samoa
    [CashType.Print]: 0,
    [CashType.Social]: 5.39,
    [CashType.Web]: 5.23,
  },
  [Country.CV]: {
    // cabo verde
    [CashType.Print]: 0,
    [CashType.Social]: 6.1,
    [CashType.Web]: 5.23,
  },
  [Country.MN]: {
    // mongolia
    [CashType.Print]: 0,
    [CashType.Social]: 5.39,
    [CashType.Web]: 5.23,
  },
  [Country.ST]: {
    // sao tome and principe
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.VU]: {
    // vanuatu
    [CashType.Print]: 0,
    [CashType.Social]: 4.54,
    [CashType.Web]: 4.45,
  },
  [Country.DM]: {
    // dominica
    [CashType.Print]: 0,
    [CashType.Social]: 8.29,
    [CashType.Web]: 8.09,
  },
  [Country.TM]: {
    // tukmenistan
    [CashType.Print]: 0,
    [CashType.Social]: 8.29,
    [CashType.Web]: 8.09,
  },
  [Country.GL]: {
    // greenland
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.JE]: {
    // jersey
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.GU]: {
    // guam
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.LI]: {
    // liechenstein
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.FJ]: {
    // fiji
    [CashType.Print]: 0,
    [CashType.Social]: 4.45,
    [CashType.Web]: 6.13,
  },
  [Country.IM]: {
    // isle of man
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.SO]: {
    // somalia
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.FO]: {
    // faroe islands
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.TC]: {
    // turks and caicos islands
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.ER]: {
    // eritrea
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.AW]: {
    // aruba
    [CashType.Print]: 0,
    [CashType.Social]: 24.64,
    [CashType.Web]: 18.27,
  },
  [Country.KG]: {
    // kyrgyzstan
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.SX]: {
    // sint marteen
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.TD]: {
    // chad
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.ME]: {
    // montenegro
    [CashType.Print]: 0,
    [CashType.Social]: 8.29,
    [CashType.Web]: 8.09,
  },
  [Country.AX]: {
    // aland islands
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.GD]: {
    // grenada
    [CashType.Print]: 0,
    [CashType.Social]: 8.93,
    [CashType.Web]: 10.27,
  },
  [Country.GW]: {
    // guinea bissau
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.VA]: {
    // vatican city
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.SC]: {
    // seychelles
    [CashType.Print]: 0,
    [CashType.Social]: 15.16,
    [CashType.Web]: 11.77,
  },
  [Country.GS]: {
    // south georgia
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.SZ]: {
    // swaziland
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.LS]: {
    // lesotho
    [CashType.Print]: 0,
    [CashType.Social]: 5.29,
    [CashType.Web]: 4.93,
  },
  [Country.GG]: {
    // guernsey
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.IO]: {
    // british indian ocean territory
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.MO]: {
    // macao
    [CashType.Print]: 0,
    [CashType.Social]: 18.39,
    [CashType.Web]: 15.95,
  },
  [Country.PW]: {
    // palau
    [CashType.Print]: 0,
    [CashType.Social]: 15.16,
    [CashType.Web]: 11.77,
  },
  [Country.TO]: {
    // tonga
    [CashType.Print]: 0,
    [CashType.Social]: 9.86,
    [CashType.Web]: 14.33,
  },
};

export const defaultEMV = {
  [CashType.Print]: 3.32,
  [CashType.Social]: 6.1,
  [CashType.Web]: 5.23,
};
